import { useTranslation } from 'react-i18next'
import type { FinancialScenarioId } from '../hooks/useTestDataForm'
import type { FinancialScenarioPayload } from '../types/testdata.types'
import { FINANCIAL_SCENARIO_OPTIONS } from '../hooks/useTestDataForm'

const LABEL_KEY_BY_SCENARIO: Record<FinancialScenarioId, string> = Object.fromEntries(
  FINANCIAL_SCENARIO_OPTIONS.map(o => [o.value, o.labelKey])
) as Record<FinancialScenarioId, string>

function fmt(amount: string, locale: string): string {
  const n = Number(amount)
  return Number.isFinite(n) ? n.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : amount
}

interface Props {
  scenarioId: FinancialScenarioId | undefined
  locale: string
  preview: FinancialScenarioPayload | null
  loading: boolean
  error: string | null
}

/** REQ-TESTDATA-007 (CAP-003 v1.2) — Financial Scenario Preview panel: a
 * read-only rendering of the same canonical `FinancialScenarioResult`
 * already materialized into `LinesEditor`'s rows (via `_financial_scenario_dict`
 * on the backend) — never a second/independent calculation, never
 * client-side arithmetic. Shown only while a supported scenario is active. */
export function FinancialScenarioPreviewPanel({ scenarioId, locale, preview, loading, error }: Props) {
  const { t } = useTranslation()
  if (!scenarioId) return null

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
          {t('test_data_generator.financial_scenario.preview_title')}
        </h3>
        {loading && (
          <span className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500" role="status">
            <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            {t('test_data_generator.financial_scenario.preview_loading')}
          </span>
        )}
      </div>

      {error && (
        <div
          className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-xs font-medium"
          role="alert"
        >
          <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          {error}
        </div>
      )}

      {!preview && !loading && !error && (
        <p className="text-xs text-gray-400 dark:text-gray-500 italic">{t('test_data_generator.financial_scenario.preview_not_ready')}</p>
      )}

      {preview && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-1">
            <div className="flex justify-between text-gray-500 dark:text-gray-400">
              <span>{t('test_data_generator.financial_scenario.preview_scenario_id')}</span>
              <span className="font-mono text-gray-700 dark:text-gray-300">{preview.scenarioId} (v{preview.version})</span>
            </div>
            <div className="flex justify-between text-gray-500 dark:text-gray-400">
              <span>{t('test_data_generator.financial_scenario.preview_name')}</span>
              <span className="text-gray-700 dark:text-gray-300">{t(LABEL_KEY_BY_SCENARIO[scenarioId])}</span>
            </div>
            <div className="flex justify-between text-gray-500 dark:text-gray-400">
              <span>{t('test_data_generator.financial_scenario.preview_line_count')}</span>
              <span className="font-mono text-gray-700 dark:text-gray-300">{preview.lines.length}</span>
            </div>
            {preview.documentAllowance && (
              <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700/50 space-y-1">
                <div className="flex justify-between text-gray-500 dark:text-gray-400">
                  <span>{t('test_data_generator.financial_scenario.preview_document_allowance')}</span>
                  <span className="font-mono text-gray-700 dark:text-gray-300">
                    {fmt(preview.documentAllowance.amount, locale)} ({preview.documentAllowance.targetVatCategory} {preview.documentAllowance.targetVatRate}%)
                  </span>
                </div>
                <div className="flex justify-between text-gray-500 dark:text-gray-400 text-xs">
                  <span>{t('test_data_generator.financial_scenario.preview_taxable_base_before_after')}</span>
                  <span className="font-mono text-gray-600 dark:text-gray-400">
                    {fmt(preview.documentAllowance.taxableBaseBefore, locale)} → {fmt(preview.documentAllowance.taxableBaseAfter, locale)}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-1">
            {preview.taxBreakdown.map(row => (
              <div key={`${row.category}-${row.rate}`} className="flex justify-between text-gray-500 dark:text-gray-400">
                <span>{row.category} ({row.rate}%)</span>
                <span className="font-mono text-gray-700 dark:text-gray-300">
                  {fmt(row.taxableBase, locale)} → {fmt(row.taxAmount, locale)}
                </span>
              </div>
            ))}
            <div className="flex justify-between text-gray-500 dark:text-gray-400 pt-1 mt-1 border-t border-gray-100 dark:border-gray-700/50">
              <span>{t('test_data_generator.financial_scenario.preview_tax_exclusive')}</span>
              <span className="font-mono text-gray-700 dark:text-gray-300">{fmt(preview.totals.taxExclusiveTotal, locale)}</span>
            </div>
            <div className="flex justify-between text-gray-500 dark:text-gray-400">
              <span>{t('test_data_generator.financial_scenario.preview_tax_total')}</span>
              <span className="font-mono text-gray-700 dark:text-gray-300">{fmt(preview.totals.taxTotal, locale)}</span>
            </div>
            <div className="flex justify-between font-semibold text-gray-800 dark:text-gray-200">
              <span>{t('test_data_generator.financial_scenario.preview_tax_inclusive')}</span>
              <span className="font-mono text-blue-600 dark:text-blue-400">{fmt(preview.totals.taxInclusiveTotal, locale)}</span>
            </div>
            {preview.totals.prepaidAmount !== '0' && preview.totals.prepaidAmount !== '0.00' && (
              <>
                <div className="flex justify-between text-gray-500 dark:text-gray-400">
                  <span>{t('test_data_generator.financial_scenario.preview_prepaid')}</span>
                  <span className="font-mono text-gray-700 dark:text-gray-300">{fmt(preview.totals.prepaidAmount, locale)}</span>
                </div>
                <div className="flex justify-between font-semibold text-gray-800 dark:text-gray-200">
                  <span>{t('test_data_generator.financial_scenario.preview_payable')}</span>
                  <span className="font-mono text-green-600 dark:text-green-400">{fmt(preview.totals.payableAmount, locale)}</span>
                </div>
              </>
            )}
            {!preview.reconciliationChecks.every(c => c.result) && (
              <div className="flex items-center gap-1.5 mt-2 text-xs text-red-600 dark:text-red-400">
                <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
                {t('test_data_generator.financial_scenario.preview_reconciliation_failed')}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
